import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { formatDateTime } from "@/utils/date";
import { toast } from "sonner";
import i18n from "@/i18n";
import { getAllInterviewLinks } from "../../../services/interview.service";
import type { InterviewLinkDetail } from "../../../types/interview.types";
import { ExtendInterviewModal } from "../../dashboard/ExtendInterviewModal";
import { Link2 } from "lucide-react";
import { DataTableOrganism } from "@/components/ui/organisms";
import {
  AccentBarHeading,
  IllustratedEmptyState,
  SummaryStatCard,
} from "@/components/ui/molecules";
import { SummarizedListSectionTemplate } from "@/components/ui/templates";
import {
  createInterviewLinksTableColumns,
  mapInterviewLinkToRow,
} from "./interviewLinksTable.columns";

interface InterviewLinksListProps {
  isAdmin: boolean;
  isLoadingAuth: boolean;
}

function interviewLinkIsExpired(dateString: string | null): boolean {
  if (!dateString) return false;
  return new Date(dateString) < new Date();
}

const mapRowOptions = {
  formatDate: formatDateTime,
  isExpired: interviewLinkIsExpired,
};

export const InterviewLinksList: React.FC<InterviewLinksListProps> = ({
  isAdmin,
  isLoadingAuth,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [interviewLinks, setInterviewLinks] = useState<InterviewLinkDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [extendModal, setExtendModal] = useState<{
    isOpen: boolean;
    interviewId: string;
    candidateName: string;
    currentExpiration: string | null;
  }>({
    isOpen: false,
    interviewId: "",
    candidateName: "",
    currentExpiration: null,
  });

  useEffect(() => {
    const fetchInterviewLinks = async () => {
      if (!isAdmin) {
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        setError(null);
        const accessToken = await getAccessTokenSilently();

        const response = await getAllInterviewLinks(accessToken);

        if (response.error) {
          let errorMessage = i18n.t('common:toasts.unexpected_error');
          if (typeof response.error === "string" && response.error.trim() !== "") {
            errorMessage = response.error;
          } else if (
            typeof response.error === "object" &&
            response.error !== null &&
            "message" in response.error
          ) {
            const potentialMessage = (response.error as { message?: unknown }).message;
            if (typeof potentialMessage === "string" && potentialMessage.trim() !== "") {
              errorMessage = potentialMessage;
            }
          }
          throw new Error(errorMessage);
        }

        if (response.data) {
          setInterviewLinks(response.data);
        } else {
          setInterviewLinks([]);
        }
      } catch (errUnknown: unknown) {
        let errorMessage = i18n.t('common:toasts.unexpected_error');
        if (errUnknown instanceof Error) {
          errorMessage = errUnknown.message !== i18n.t('common:toasts.unexpected_error', { lng: 'en' })
            ? errUnknown.message
            : i18n.t('common:toasts.unexpected_error');
        }
        setError(errorMessage);
        toast.error(errorMessage);
        console.error("Error fetching interview links:", errUnknown);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isLoadingAuth && isAdmin) {
      fetchInterviewLinks();
    } else if (!isLoadingAuth && !isAdmin) {
      setIsLoading(false);
    }
  }, [isAdmin, isLoadingAuth, getAccessTokenSilently]);

  const handleExtendSuccess = () => {
    const fetchInterviewLinks = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await getAllInterviewLinks(accessToken);

        if (response.error) {
          throw new Error(response.error as string);
        }

        if (response.data) {
          setInterviewLinks(response.data);
        }
      } catch (err) {
        console.error("Error refreshing interview links:", err);
      }
    };

    void fetchInterviewLinks();
  };

  const openExtendModal = useCallback(
    (interviewId: string, candidateName: string, currentExpiration: string | null) => {
      setExtendModal({
        isOpen: true,
        interviewId,
        candidateName,
        currentExpiration,
      });
    },
    []
  );

  const closeExtendModal = () => {
    setExtendModal({
      isOpen: false,
      interviewId: "",
      candidateName: "",
      currentExpiration: null,
    });
  };

  const getInterviewUrl = useCallback((token: string | null) => {
    if (!token) return "#";
    return `/interview/${token}`;
  }, []);

  const tableRows = useMemo(
    () => interviewLinks.map((link) => mapInterviewLinkToRow(link, mapRowOptions)),
    [interviewLinks]
  );

  const linkColumns = useMemo(
    () =>
      createInterviewLinksTableColumns({
        getInterviewUrl,
        onExtend: openExtendModal,
      }),
    [getInterviewUrl, openExtendModal]
  );

  const emptyState = useMemo(() => {
    if (interviewLinks.length === 0) {
      return (
        <IllustratedEmptyState
          icon={<Link2 />}
          title="No interview links"
          description="No interview links found in the system."
        />
      );
    }
    return (
      <IllustratedEmptyState
        icon={<Link2 />}
        title="No matching interview links"
        description="Try adjusting your search by company, email, job, candidate, or status."
      />
    );
  }, [interviewLinks.length]);

  if (!isAdmin) {
    return null;
  }

  if (isLoadingAuth || isLoading) {
    return (
      <SummarizedListSectionTemplate
        status="loading"
        loadingMessage="Loading interview links…"
      />
    );
  }

  if (error) {
    return (
      <SummarizedListSectionTemplate
        status="error"
        errorTitle="Error loading links"
        errorMessage={error}
      />
    );
  }

  return (
    <>
      <SummarizedListSectionTemplate
        status="ready"
        sectionGap="relaxed"
        summary={
          <SummaryStatCard
            label="Active interview links"
            value={interviewLinks.length}
            icon={<Link2 className="text-gradient-pink" />}
          />
        }
        primary={
          <DataTableOrganism
            columns={linkColumns}
            data={tableRows}
            pageSize={10}
            enableSelection
            enableSearch
            enableColumnVisibility
            columnVisibilityLabel="Column visibility"
            toolbarTitle={
              <AccentBarHeading>Interview links management</AccentBarHeading>
            }
            searchPlaceholder="Search by company, email, job, candidate…"
            searchKeys={[
              "jobTitle",
              "companyName",
              "creatorEmail",
              "candidateName",
              "candidateEmail",
              "statusDisplay",
            ]}
            paginationItemLabel="links"
            dataTableProps={{
              emptyState,
            }}
          />
        }
      />

      <ExtendInterviewModal
        isOpen={extendModal.isOpen}
        onClose={closeExtendModal}
        interviewId={extendModal.interviewId}
        candidateName={extendModal.candidateName}
        currentExpiration={extendModal.currentExpiration}
        onSuccess={handleExtendSuccess}
      />
    </>
  );
};
